import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import './HeroLearningDemo.css';

const SCENE_COUNT = 7;
const SCENE_DURATION = 2500; // ms per scene

const SCENE_KEYS = [
  'vocabulary',
  'sentenceStructure',
  'dictation',
  'translation',
  'shadowing',
  'writing',
  'aiChat',
] as const;

/* ─── Scene 1: Vocabulary ─── */
const VocabularyScene: React.FC<{ active: boolean; t: (key: string) => string }> = ({ active, t }) => (
  <div className={`demo-scene scene-vocabulary ${active ? 'active' : ''}`}>
    <div className="vocab-cefr-badge">B1</div>
    <div className="vocab-word">approach</div>
    <div className="vocab-phonetic">/əˈproʊtʃ/</div>
    <div className="vocab-definition">
      <span className="vocab-typewriter">{t('heroDemo.vocab.definition')}</span>
    </div>
    <div className="vocab-mastery">+1</div>
  </div>
);

/* ─── Scene 2: Dependency Graph ─── */
const DependencyScene: React.FC<{ active: boolean }> = ({ active }) => (
  <div className={`demo-scene scene-dependency ${active ? 'active' : ''}`}>
    <svg className="dep-svg" viewBox="0 0 380 120" preserveAspectRatio="xMidYMid meet">
      {/* nsubj: She(25) → looking(170) */}
      <path className="dep-arc arc-1" d="M 25,85 C 25,35 170,35 170,85" fill="none" stroke="#3b82f6" strokeWidth="1.8" />
      <text className="dep-arc-label arc-label-1" x="97" y="46" fill="#3b82f6" fontSize="9" textAnchor="middle" fontWeight="600">nsubj</text>
      {/* aux: has(75) → looking(170) */}
      <path className="dep-arc arc-2" d="M 75,85 C 75,55 170,55 170,85" fill="none" stroke="#8b5cf6" strokeWidth="1.8" />
      <text className="dep-arc-label arc-label-2" x="122" y="62" fill="#8b5cf6" fontSize="9" textAnchor="middle" fontWeight="600">aux</text>
      {/* obl: looking(170) → you(355) */}
      <path className="dep-arc arc-3" d="M 170,85 C 170,25 355,25 355,85" fill="none" stroke="#f59e0b" strokeWidth="1.8" />
      <text className="dep-arc-label arc-label-3" x="262" y="36" fill="#f59e0b" fontSize="9" textAnchor="middle" fontWeight="600">obl</text>
      {/* Words */}
      <text className="dep-word dep-w1" x="25" y="100" fontSize="13" fontWeight="600" fill="#1e293b" textAnchor="middle">She</text>
      <text className="dep-word dep-w2" x="75" y="100" fontSize="13" fontWeight="600" fill="#1e293b" textAnchor="middle">has</text>
      <text className="dep-word dep-w3" x="120" y="100" fontSize="13" fontWeight="600" fill="#1e293b" textAnchor="middle">been</text>
      <text className="dep-word dep-w4" x="170" y="100" fontSize="13" fontWeight="700" fill="#3b82f6" textAnchor="middle">looking</text>
      <text className="dep-word dep-w5" x="225" y="100" fontSize="13" fontWeight="600" fill="#1e293b" textAnchor="middle">forward</text>
      <text className="dep-word dep-w6" x="270" y="100" fontSize="13" fontWeight="600" fill="#1e293b" textAnchor="middle">to</text>
      <text className="dep-word dep-w7" x="310" y="100" fontSize="13" fontWeight="600" fill="#1e293b" textAnchor="middle">meeting</text>
      <text className="dep-word dep-w8" x="355" y="100" fontSize="13" fontWeight="600" fill="#1e293b" textAnchor="middle">you</text>
    </svg>
  </div>
);

/* ─── Scene 3: Dictation ─── */
const DictationScene: React.FC<{ active: boolean; t: (key: string) => string }> = ({ active, t }) => (
  <div className={`demo-scene scene-dictation ${active ? 'active' : ''}`}>
    <div className="dict-wave-bars">
      {[...Array(7)].map((_, i) => (
        <div key={i} className="dict-bar" style={{ animationDelay: `${i * 0.08}s` }} />
      ))}
    </div>
    <div className="dict-text">
      <span className="dict-typed">I look </span>
      <span className="dict-wrong">foward</span>
      <span className="dict-arrow">&rarr;</span>
      <span className="dict-correct">forward</span>
      <span className="dict-typed"> to it</span>
    </div>
    <div className="dict-accuracy">{t('heroDemo.dict.accuracy')}</div>
  </div>
);

/* ─── Scene 4: Translation + AI ─── */
const TranslationScene: React.FC<{ active: boolean; t: (key: string) => string }> = ({ active, t }) => (
  <div className={`demo-scene scene-translation ${active ? 'active' : ''}`}>
    <div className="trans-en">I've been <span className="trans-highlight">looking forward to</span> meeting you.</div>
    <div className="trans-zh">{t('heroDemo.trans.translation')}</div>
    <div className="trans-ai-bubble">
      <span className="trans-ai-icon">🤖</span>
      <span className="trans-ai-text">{t('heroDemo.trans.aiExplanation')}</span>
    </div>
  </div>
);

/* ─── Scene 5: Shadowing ─── */
const ShadowingScene: React.FC<{ active: boolean; t: (key: string) => string }> = ({ active, t }) => (
  <div className={`demo-scene scene-shadowing ${active ? 'active' : ''}`}>
    <div className="shadow-rec-indicator">
      <span className="shadow-rec-dot" />
      REC
    </div>
    <div className="shadow-wave-bars">
      {[...Array(7)].map((_, i) => (
        <div key={i} className="shadow-bar" style={{ animationDelay: `${i * 0.1}s` }} />
      ))}
    </div>
    <div className="shadow-score-circle">
      <svg viewBox="0 0 80 80">
        <circle className="shadow-score-bg" cx="40" cy="40" r="34" />
        <circle className="shadow-score-fill" cx="40" cy="40" r="34" />
      </svg>
      <span className="shadow-score-num">92</span>
    </div>
    <div className="shadow-sub-scores">
      <div className="shadow-sub"><span className="shadow-sub-label">{t('heroDemo.shadow.pronunciation')}</span><div className="shadow-sub-bar"><div className="shadow-sub-fill" style={{ width: '90%' }} /></div></div>
      <div className="shadow-sub"><span className="shadow-sub-label">{t('heroDemo.shadow.fluency')}</span><div className="shadow-sub-bar"><div className="shadow-sub-fill" style={{ width: '88%' }} /></div></div>
      <div className="shadow-sub"><span className="shadow-sub-label">{t('heroDemo.shadow.intonation')}</span><div className="shadow-sub-bar"><div className="shadow-sub-fill" style={{ width: '95%' }} /></div></div>
    </div>
  </div>
);

/* ─── Scene 6: Writing ─── */
const WritingScene: React.FC<{ active: boolean; t: (key: string) => string }> = ({ active, t }) => (
  <div className={`demo-scene scene-writing ${active ? 'active' : ''}`}>
    <div className="writing-prompt">{t('heroDemo.writing.prompt')}</div>
    <div className="writing-input">
      <span className="writing-typed">She is both smart and kind.</span>
      <span className="writing-cursor" />
    </div>
    <div className="writing-feedback">
      <div className="writing-correct">✅ {t('heroDemo.writing.grammarCorrect')}</div>
      <div className="writing-suggestion">💡 {t('heroDemo.writing.suggestion')}</div>
    </div>
    <div className="writing-score">85</div>
  </div>
);

/* ─── Scene 7: AI Chat ─── */
const ChatScene: React.FC<{ active: boolean; t: (key: string) => string }> = ({ active, t }) => (
  <div className={`demo-scene scene-chat ${active ? 'active' : ''}`}>
    <div className="chat-bubble chat-user">
      {t('heroDemo.chat.userMsg')}
    </div>
    <div className="chat-typing">
      <span /><span /><span />
    </div>
    <div className="chat-bubble chat-ai">
      {t('heroDemo.chat.aiMsg')}
    </div>
    <div className="chat-input-bar">
      <div className="chat-input-placeholder">{t('heroDemo.chat.placeholder')}</div>
      <div className="chat-send-btn">↑</div>
    </div>
  </div>
);

const HeroLearningDemo: React.FC = () => {
  const { t } = useTranslation('home');
  const [activeScene, setActiveScene] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const sceneLabels = SCENE_KEYS.map((key) => t(`heroDemo.scenes.${key}`));

  const goToScene = useCallback((index: number) => {
    setActiveScene(index);
    setIsPaused(true);
    setTimeout(() => setIsPaused(false), 50);
  }, []);

  useEffect(() => {
    if (isPaused) return;
    const timer = setInterval(() => {
      setActiveScene((prev) => (prev + 1) % SCENE_COUNT);
    }, SCENE_DURATION);
    return () => clearInterval(timer);
  }, [isPaused]);

  const renderScene = (index: number) => {
    const active = index === activeScene;
    switch (index) {
      case 0: return <VocabularyScene key={0} active={active} t={t} />;
      case 1: return <DependencyScene key={1} active={active} />;
      case 2: return <DictationScene key={2} active={active} t={t} />;
      case 3: return <TranslationScene key={3} active={active} t={t} />;
      case 4: return <ShadowingScene key={4} active={active} t={t} />;
      case 5: return <WritingScene key={5} active={active} t={t} />;
      case 6: return <ChatScene key={6} active={active} t={t} />;
      default: return null;
    }
  };

  return (
    <div className="hero-demo" role="img" aria-label={t('heroDemo.ariaLabel')}>
      <div className="demo-phone">
        <div className="demo-notch" />
        <div className="demo-status-bar">
          <span>9:41</span>
          <span className="demo-battery">🔋</span>
        </div>

        <div className="demo-screen">
          {Array.from({ length: SCENE_COUNT }, (_, i) => renderScene(i))}
        </div>

        <div className="demo-home-bar" />
      </div>

      <div className="demo-indicators">
        {sceneLabels.map((label, i) => (
          <button
            key={i}
            className={`demo-dot ${i === activeScene ? 'active' : ''}`}
            onClick={() => goToScene(i)}
            aria-label={`Scene ${i + 1}: ${label}`}
          />
        ))}
      </div>
      <div className="demo-label">{sceneLabels[activeScene]}</div>
    </div>
  );
};

export default HeroLearningDemo;
